import React from 'react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from 'react-query';
/** Components */
import { PageHelper } from 'Components/PageHelper';
import { Card } from 'Components/Card';
import { Column, Row } from 'Components/Grid';
import { Container } from 'Components/Container';
import { CustomTextField } from 'Components/Form/styles';
import { DefaultTable } from 'Components/DefaultTable';
import { ModalConfirmDelete } from 'Components/ModalConfirmDelete';
import { NoData } from 'Components/NoData';
import { PageLoaderList } from 'Components/PageLoader/List';
import { tableHeader } from './table-header';
/** Services */
import { deletePolicy, getPoliciesPaged } from 'services/policies-service';
/** State Managers */
import { useAuthTenant, useAuthUser } from 'store/auth';
import { useSelector } from 'react-redux';
/** Third Lib Modal */
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { Pagination, Stack, Button } from '@mui/material';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { useTranslation } from 'react-i18next';

export const Policies = () => {
  const { t } = useTranslation(['policy']);
  const user = useSelector(useAuthUser);
  const selectedTenant = useSelector(useAuthTenant);
  const SwalReact = withReactContent(Swal);
  const [open, setOpen] = useState(false);
  const [refetch, setRefetch] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [page, setPage] = useState(1);
  const [statusFilter] = useState('');
  const { register, watch } = useForm({
    defaultValues: {
      searchContent: '',
    },
  });
  const getContentSearch = () => {
    if (!watch('searchContent')) return '';

    return watch('searchContent');
  };

  React.useEffect(() => {
    setPage(1);
    setRefetch(r => !r);
  }, [statusFilter]);

  const {
    isLoading,
    error,
    data: pagedPolicies,
  } = useQuery(['getPolicy', refetch, page, statusFilter], async () => {
    const result = await getPoliciesPaged(selectedTenant.slug, page, 15, getContentSearch(), statusFilter);
    if (result?.data.data.length > 0) {
      return result?.data;
    }
    return {
      data: [],
      page,
      totalPages: 0,
    };
  });

  const onDelete = item => {
    setItemToDelete(item);
    setOpen(true);
  };

  const onClose = () => {
    setItemToDelete(null);
    setOpen(false);
  };

  const onConfirm = async () => {
    try {
      const result = await deletePolicy(itemToDelete?._id);
      if (result.status === 204) {
        onClose();
        setRefetch(!refetch);
        SwalReact.fire({
          title: t('policy:page.delete.successTitle'),
          text: t('policy:page.delete.success'),
          icon: 'success',
        });
      } else {
        throw new Error(t('policy:page.delete.errorTitle'));
      }
    } catch (error) {
      SwalReact.fire({
        title: t('policy:page.delete.errorTitle'),
        text: error.response.data.message ?? error.message,
        icon: 'error',
      });
    }
  };

  const tourSteps = [
    {
      target: 'body',
      content: t('policy:page.tour.welcome'),
      disableBeacon: true,
      disableScrolling: true,
      placement: 'center',
    },
    {
      target: '[data-tour="page-title"] button',
      content: t('policy:page.tour.addButton'),
      disableScrolling: true,
    },
    {
      target: '[data-tour="filter-card"]',
      content: t('policy:page.tour.filterCard'),
      disableScrolling: true,
    },
    {
      target: '[data-tour="filter-name"]',
      content: t('policy:page.tour.filterName'),
      disableScrolling: false,
    },
    {
      target: '[data-tour="filter-button"]',
      content: t('policy:page.tour.filterButton'),
      disableScrolling: false,
    },
    {
      target: '[data-tour="table"]',
      content: t('policy:page.tour.table'),
      disableScrolling: false,
    },
    {
      target: '[data-tour="pagination"]',
      content: t('policy:page.tour.pagination'),
      disableScrolling: false,
    },
    {
      target: 'body',
      content: t('policy:page.tour.complete'),
      disableScrolling: true,
      placement: 'center',
    },
  ];

  return (
    <Container title={t('policy:page.title')} breadcrumb={t('policy:page.breadcrumb')} showButtonAdd linkPage="/policies" data-tour="page-title">
      {isLoading && <PageLoaderList></PageLoaderList>}
      {error && <div>{t('policy:page.error')} {error.message}</div>}
      {!isLoading && !error && pagedPolicies.data?.length === 0 && (
        <>
          <Card className="mb-2 mt-3" data-tour="filter-card">
            <Row sx={{ alignItems: 'center' }}>
              <Column xs={12} md={8}>
              <div data-tour="filter-name" style={{ width: '100%' }}>
              <CustomTextField
                {...register('searchContent')}
                label={t('policy:page.search.label')}
                sx={{
                  minHeight: '40px',
                  maxHeight: '40px',
                  margin: '0 auto',
                  width: '100%',
                  ' > div': {
                    minHeight: '40px',
                  },
                }}
                InputLabelProps={{ shrink: true }}
                autoComplete="off"
                 />
              </div>
              </Column>
              <Column xs={12} md={4} style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                <div data-tour="filter-button" style={{ width: '100%' }}>
                <Button
                  size="small"
                  variant="contained"
                  sx={{
                    width: '120px',
                    borderRadius: '8px',
                    padding: '8px 20px',
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    height: '36px',
                    fontWeight: 500,
                  }}
                  endIcon={<SearchOutlinedIcon />}
                  onClick={() => { setPage(1); setRefetch(!refetch); }}
                >
                  {t('policy:page.search.button')}
                </Button>
                </div>
              </Column>
            </Row>
          </Card>
          <NoData description={t('policy:page.noData')} />
        </>
      )}
      {!isLoading && !error && pagedPolicies.data?.length > 0 && (
        <div className="container">
          <Card className="mt-3" data-tour="filter-card">
            <Row sx={{ alignItems: 'center' }}>
              <Column xs={12} md={8}>
              <div data-tour="filter-name" style={{ width: '100%' }}>
              <CustomTextField
                {...register('searchContent')}
                label={t('policy:page.search.label')}
                sx={{
                  minHeight: '40px',
                  maxHeight: '40px',
                  width: '100%',
                  ' > div': {
                    minHeight: '40px',
                  },
                }}
                InputLabelProps={{ shrink: true }}
              />
              </div>
              </Column>
              <Column xs={12} md={4} style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                <div data-tour="filter-button" style={{ width: '100%' }}>
                <Button
                  size="small"
                  variant="contained"
                  sx={{
                    width: '120px',
                    borderRadius: '8px',
                    padding: '8px 20px',
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    height: '36px',
                    fontWeight: 500,
                  }}
                  endIcon={<SearchOutlinedIcon />}
                  onClick={() => { setPage(1); setRefetch(!refetch); }}
                >
                  {t('policy:page.search.button')}
                </Button>
                </div>
              </Column>
            </Row>
          </Card>
          <div data-tour="table">
          <DefaultTable
            rows={tableHeader(t)}
            cells={pagedPolicies.data}
            userId={user.userId}
            onClickDelete={onDelete}
            link="policies"
          />
          </div>
          <div data-tour="pagination">
          <Stack
            sx={{
              justifyContent: 'center',
              flexDirection: 'row',
              marginTop: '16px',
            }}
          >
            <Pagination
              count={pagedPolicies.totalPages}
              onChange={(_, selectedPage) => {
                setPage(selectedPage);
              }}
              color="primary"
              page={page}
              showFirstButton
              showLastButton
            />
          </Stack>
          </div>
          <ModalConfirmDelete
            description={
              <>
                Tem certeza que deseja excluir <strong>{` ${itemToDelete?.name} `}</strong> como ? de negócio?
              </>
            }
            open={open}
            onClose={onClose}
            onConfirm={onConfirm}
          />
        </div>
      )}
      <PageHelper steps={tourSteps} tourKey="policies-list" />
    </Container>
  );
};
